// The Map Review ledger's resource-QUERY translation (doc/in-progress/query.md §11
// Phase 3c).
//
// The workbench's ledger — its page, the eight counts, the footer and the map
// highlights — is served by the generic per-resource endpoints: one `QUERY
// /api/<resource>` per ledger table, `POST /api/summary` for the footer, and a
// minted identity set per table for the map. This module is the translation from
// the store's shared filter spec — the retired Map Review query body, whose
// spec states every table's narrowing at once — to that per-resource grammar: one
// table's own clauses, the spatial narrowing, the ordering and the selection.
//
// It is a pure function of the spec, the capability documents and the page, so
// the translation is reviewable and testable on its own and the store keeps only
// the orchestration.

import type {
	MapReviewCapabilities,
	MapReviewCapabilityProperty,
	MapReviewColumnFilter,
	MapReviewSelection,
	MapReviewQueryBodyFilter,
	MapReviewQueryBodyLocation,
	MapReviewQueryBodyOrder,
	MapReviewQueryIdentitySetInfo,
	MapReviewSort,
	MapReviewSpec,
	MapReviewTable,
} from "@/type/api";

import {
	RELATION_COLUMN,
	clientZoneOffsetMinutes,
	columnByKey,
} from "./columns";

/**
 * The route each ledger table is served at (api/routes.go). `LEDGER_QUERY_PATH`
 * is keyed by the table because the two are the same word everywhere it matters:
 * the resource's descriptor is named from the table it mirrors
 * (resource/ledger.go), which is also the name `POST /api/summary` and the
 * tile-side `<resource>_selection_token` predicate expect. Only the path's
 * spelling differs — `trap_station` is served at `/api/trap-station`.
 */
export const LEDGER_QUERY_PATH: Record<MapReviewTable, string> = {
	site: "/api/site",
	feature: "/api/feature",
	treatment: "/api/treatment",
	observation: "/api/observation",
	trap_station: "/api/trap-station",
	trap_instance: "/api/trap-instance",
	species_count: "/api/species-count",
	trap_sample: "/api/trap-sample",
	zone: "/api/zone",
	// The lab's own resource (resource/pool.go), served beside the species
	// count it belongs to: it is a ledger table without being one of the review
	// workbench's tabs.
	pool: "/api/pool",
};

/** The applied-rate column's key, as the server's registry declares it
 * (`map_review.AppliedRateColumnKey`) and as `columns.ts` mirrors it. */
const APPLIED_RATE_COLUMN = "applied_rate";

/** The `over` member of the verdict vocabulary (`map_review.RateVerdictOver`). */
const RATE_VERDICT_OVER = "over";

/**
 * The clause that resolves the district's over-label treatments — the map's
 * over-rate set. It is a real clause on the `treatment` resource in the generic
 * layer (platform/query_treatment.go's derived predicate), so the set is the
 * spec's own narrowing intersected with the over verdict, exactly as the
 * resolver's `over_rate_ids` was.
 */
export const OVER_RATE_CLAUSE: MapReviewQueryBodyFilter = {
	property: APPLIED_RATE_COLUMN,
	operator: "equal",
	value: RATE_VERDICT_OVER,
};

/**
 * One clause's value on the wire. The generic gate types every value by the
 * property it names (queryspec.resolveValue): a boolean property's members are
 * JSON booleans, a measure's or an id-backed enum's are JSON numbers, and every
 * other member is its own string. The legacy body carried a boolean as the
 * string "true"/"false" — the resolver parsed it, the generic gate does not — so
 * the shape is derived from the server's own declaration of the property, with
 * the presentation registry's kind as the fallback while a capability document is
 * missing.
 */
function wireMember(
	table: MapReviewTable,
	column: string,
	value: string | number,
	property: MapReviewCapabilityProperty | undefined,
): string | number | boolean {
	const declared = columnByKey(table, column);
	if (property ? property.type === "bool" : declared?.kind === "bool") {
		// The control's members travel as the strings "true"/"false" (the legacy
		// body's spelling, which the resolver parsed with ParseBool); the generic
		// gate wants JSON booleans (queryspec.boolMembers).
		return value === "true";
	}
	// An id-backed enum (a product, an intervention, a technician) and a runtime
	// one (a trap type, a lure, a species) carry numbers; so does a measure.
	const numeric = property
		? property.type === "int" ||
			property.type === "float" ||
			property.id_source !== undefined ||
			property.runtime === true
		: declared?.optionValueKind === "number" || declared?.kind === "number";
	if (numeric) return typeof value === "number" ? value : Number(value);
	return String(value);
}

/** One stored clause, as the clauses a resource QUERY states. A clause can
 * become two (a range has two bounds) or none (a control cleared to an empty
 * value), so this answers a list. */
function clausesFor(
	clause: MapReviewColumnFilter,
	capabilities: MapReviewCapabilities,
): MapReviewQueryBodyFilter[] {
	const property = capabilities[clause.table]?.properties.find(
		(declared) => declared.name === clause.column,
	);
	switch (clause.op) {
		case "contains":
			return clause.text
				? [
						{
							property: clause.column,
							operator: "contains",
							value: clause.text,
						},
					]
				: [];
		case "enum": {
			const members = (clause.values ?? []).map((value) =>
				wireMember(clause.table, clause.column, value, property),
			);
			if (members.length === 0) return [];
			return [
				{
					property: clause.column,
					operator: "equal",
					// One member travels as a scalar and several as a membership,
					// which the gate reads as the same clause either way.
					value: members.length === 1 ? members[0] : members,
				},
			];
		}
		case "numberRange": {
			// The grid's window is inclusive at both ends, exactly as the generic
			// `gte`/`lte` pair is (query.go floatRange).
			const bounds: MapReviewQueryBodyFilter[] = [];
			if (clause.min !== null && clause.min !== undefined) {
				bounds.push({
					property: clause.column,
					operator: "gte",
					value: clause.min,
				});
			}
			if (clause.max !== null && clause.max !== undefined) {
				bounds.push({
					property: clause.column,
					operator: "lte",
					value: clause.max,
				});
			}
			return bounds;
		}
		case "dateRange": {
			// A bare day: `after` means the day begins in the client's zone and
			// `before` that it ends there, in one inclusive comparison — the rule
			// the legacy resolver's tsRangeExpr built from the same offset
			// (queryspec.resolveTemporal).
			const bounds: MapReviewQueryBodyFilter[] = [];
			if (clause.from) {
				bounds.push({
					property: clause.column,
					operator: "after",
					value: clause.from,
				});
			}
			if (clause.to) {
				bounds.push({
					property: clause.column,
					operator: "before",
					value: clause.to,
				});
			}
			return bounds;
		}
		case "relation":
			return clause.source &&
				clause.source_id !== null &&
				clause.source_id !== undefined
				? [
						{
							// The reserved property name every declared edge is traversed
							// through (queryspec.RelationProperty).
							property: RELATION_COLUMN,
							operator: "relation",
							value: { source: clause.source, source_id: clause.source_id },
						},
					]
				: [];
	}
}

/**
 * `table`'s own narrowing, as a resource QUERY states it: every clause the
 * shared spec sets on that table, and only those — a clause names a property of
 * the queried resource itself (D4), so one table's clauses are never sent to
 * another. The spec's legacy cross-column search is deliberately absent from
 * this translation, and it is a different thing from the descriptor's virtual
 * `search` property (SEARCH_PROPERTY, resource/ledger.go): the finder sends
 * that property in its own request and never through `spec`, no control in the
 * ledger sets a spec search any more (the per-table search boxes were removed
 * and a restored spec clears them), and nothing the server produces fills it
 * (ask's extraction never sets a search term). A spec search term must not
 * become a ledger clause, or a find would silently narrow the page.
 */
export function ledgerFilter(
	table: MapReviewTable,
	spec: MapReviewSpec,
	capabilities: MapReviewCapabilities,
): MapReviewQueryBodyFilter[] {
	const filter: MapReviewQueryBodyFilter[] = [];
	// An explicit Feature selection travels as the identity property's own
	// membership: the Feature resource declares `id` as an int property narrowed
	// by `equal`, and a member list is its any-of form. It is the one clause the
	// spec cannot express as a column filter, and it propagates to the related
	// records through the identity set every filtered ledger read mints, exactly
	// as a Feature column clause does.
	if (table === "feature" && (spec.feature_ids?.length ?? 0) > 0) {
		filter.push({
			property: "id",
			operator: "equal",
			value: spec.feature_ids,
		});
	}
	for (const clause of spec.filters) {
		if (clause.table !== table) continue;
		filter.push(...clausesFor(clause, capabilities));
	}
	return filter;
}

/**
 * One table's filter as another table's relation clause: `table` is narrowed to
 * the rows related to the set `source`'s filter keeps, along the declared edge
 * between them (§8.1, the set form of the relation clause). It is what makes a
 * filter set on one ledger reach the records related to it — the cross-table
 * reach the legacy resolver's keep graph had (`kFeature` = ownFeature ∧ the
 * Features of the kept Treatments), now expressed with the identity set every
 * ledger read already mints.
 */
export function relationSetClause(
	source: MapReviewTable,
	token: string,
): MapReviewQueryBodyFilter {
	return {
		property: RELATION_COLUMN,
		operator: "relation",
		value: { source, identity_set: token },
	};
}

/**
 * Every relation clause `table` owes the tables that carry a filter of their
 * own: one per (other) filtered table `table` declares an edge to. The edge
 * declaration — `OPTIONS`' `relations.edges[].target` — is what keeps the clause
 * legal: a table the graph does not connect (the Zones surface, related by
 * geometry rather than by a stored reference) declares no such edge and so is
 * never sent one, and the server would refuse it 422 if it were.
 */
export function relatedSetClauses(
	table: MapReviewTable,
	filtered: readonly { table: MapReviewTable; token: string }[],
	capabilities: MapReviewCapabilities,
): MapReviewQueryBodyFilter[] {
	const declared = new Set(
		(capabilities[table]?.relations?.edges ?? []).map((edge) => edge.target),
	);
	return filtered
		.filter((f) => f.table !== table && declared.has(f.table))
		.map((f) => relationSetClause(f.table, f.token));
}

/**
 * The spatial narrowing a resource QUERY states, or nothing when the spec states
 * none. The spec's geographic inputs are the members the generic grammar carries
 * (D16, plan §7.1): the selected standing zones as `zones`, the inside buffers
 * as `polygons`, the outside buffers as `outside_polygons`, and the mode the
 * inside buffers compose by as `polygon_combine`.
 *
 * The members compose as the resolver composes its own narrowing
 * (query.go `zoneContains`): the standing zones OR the inside buffers (any of
 * them, or every one under intersection), AND NOT any outside buffer. Standing
 * zones always compose by union, whatever the buffer mode.
 */
export function ledgerLocation(
	spec: MapReviewSpec,
): MapReviewQueryBodyLocation | undefined {
	const location: MapReviewQueryBodyLocation = {};
	if (spec.zones.length > 0) location.zones = spec.zones;
	const inside = spec.temp_zones.filter((zone) => zone.inside !== false);
	const outside = spec.temp_zones.filter((zone) => zone.inside === false);
	if (inside.length > 0) {
		location.polygons = inside.map((zone) => zone.geometry);
		location.polygon_combine = spec.buffer_combine ?? "union";
	}
	if (outside.length > 0) {
		location.outside_polygons = outside.map((zone) => zone.geometry);
	}
	if (location.zones || location.polygons || location.outside_polygons) {
		return location;
	}
	return undefined;
}

/**
 * The requested ordering. `null` asks for the resource's own declared default
 * order (`map_review.DefaultSort`, which the descriptors mirror in `DefaultOrder`)
 * by stating no order at all — the ledger's default sort, and the same one the
 * legacy endpoint defaulted to.
 */
export function ledgerOrder(
	sort: MapReviewSort | null,
): MapReviewQueryBodyOrder[] | undefined {
	if (!sort) return undefined;
	return [{ property: sort.column, direction: sort.desc ? "desc" : "asc" }];
}

/**
 * The client zone offset to state, or nothing when the table carries no date
 * clause. The offset is what makes a bare day in a `before`/`after` clause mean
 * the operator's day (D30); a `date` property ignores it, so sending it whenever
 * any date clause is present is exact either way and keeps the body free of a
 * field nothing reads.
 */
export function ledgerTZOffset(
	table: MapReviewTable,
	spec: MapReviewSpec,
): number | undefined {
	const dated = spec.filters.some(
		(clause) => clause.table === table && clause.op === "dateRange",
	);
	return dated ? clientZoneOffsetMinutes() : undefined;
}

/** The selection fields the generic envelope cannot state. The legacy response
 * filled them from the coarse attribute clauses a selection too large to make
 * exact fell back to; a minted set is exact in both of its shapes, so none of
 * them is populated any more. */
const NO_COARSE_CLAUSES = {
	product_ids: null,
	start_min: null,
	start_max: null,
} as const;

/**
 * One table's map highlight, from the identity set its query minted.
 *
 * The set's ids and its token are two channels of the same set (D26/§9.3), and
 * which one a layer can use is a property of the layer, not of the set:
 *
 *   - the observation layer has no client-side filter at all — the tile IS the
 *     resolved set — so the token travels whatever the set's size, exactly as the
 *     legacy selection always shipped one.
 *   - the treatment and feature layers have a tile-side predicate
 *     (`<resource>_selection_token`, etc/tegola.toml) and a client-side id filter.
 *     Within the shipping cap the ids narrow the layer and no token travels;
 *     above it the token narrows the tiles and the layer carries no filter.
 *   - Sites, Trap Locations, Trap Instances, Pools and the two tables with no
 *     layer have no tile predicate, so a shipped set of ids is the whole
 *     highlight. Above the cap no ids travel and the layer draws unnarrowed,
 *     which `exact: false` flags to the control pane — the deliberate limit of
 *     the id channel, and the same one the legacy selection's cap drew.
 */
export function ledgerSelection(
	table: MapReviewTable,
	minted: MapReviewQueryIdentitySetInfo | undefined,
): MapReviewSelection {
	const ids = minted?.ids ?? null;
	const token = minted?.id ?? null;
	if (table === "observation") {
		return {
			ids,
			selection_token: token,
			exact: true,
			truncated: ids === null,
			...NO_COARSE_CLAUSES,
		};
	}
	if (table === "treatment" || table === "feature") {
		return {
			ids,
			selection_token: ids === null ? token : null,
			exact: true,
			truncated: ids === null,
			...NO_COARSE_CLAUSES,
		};
	}
	return {
		ids,
		selection_token: null,
		exact: ids !== null,
		truncated: ids === null,
		...NO_COARSE_CLAUSES,
	};
}
